import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

export const MODAL_SHOW = 'MODAL_SHOW';
export const MODAL_HIDE = 'MODAL_HIDE';

export type IShow = {
  component: ModalComponent;
  componentProps?: ModalComponentProps;
};

import { Toast } from '../toaster/Toast';
import { initializeToastManager } from '../toaster/ToasterImplementation';

import * as Classes from '../common/classes';

import type {
  ToastManagerPosition,
  ToastInfo,
} from '../toaster/ToasterImplementation';
import { eventEmitter } from './Modal';

type ModalInfo = {
  key: string;
  depth: number;
  component: ModalComponent;
  componentProps?: ModalComponentProps;
};

type Props = {};

export type ModalComponentProps = {
  // Pokud bude posláno v propsech, tak se zavolá při zavření dialogu (ať už ze zavíracího tlačítka dialogu, tak ručně pomocí zavolání onClose v komponentě), v e je možné zavolat preventDefault pro zabránění automatickému zavření dialogu
  onClose:
    | ((e?: React.SyntheticEvent<HTMLElement>) => void)
    | ((e?: React.MouseEvent<HTMLElement>) => void);
};

export type ClosableDialogProps = {
  // Pokud bude posláno v propsech, tak se zavolá při zavření dialogu (ať už ze zavíracího tlačítka dialogu, tak ručně pomocí zavolání onClose v komponentě), v e je možné zavolat preventDefault pro zabránění automatickému zavření dialogu
  onClose?:
    | ((e?: React.SyntheticEvent<HTMLElement>) => void)
    | ((e?: React.MouseEvent<HTMLElement>) => void);
};

export type ModalComponent = React.ComponentType<ModalComponentProps>;

export const ModalManager: React.FC<Props> = () => {
  const keyref = useRef<number>(1);
  // @ts-ignore
  const [modalsState, setModalsState] = useState<ModalInfo[]>([]);
  const modals = useRef<{ [key: string]: ModalInfo }>({});

  useLayoutEffect(() => {
    // Do document.body přidáme div s id ui-v4-modal-root, do kterého se budou renderovat modaly, ale jen pokud tam ještě není
    const modalRoot = document.getElementById('ui-v4-modal-root');
    if (!modalRoot) {
      const modalRoot = document.createElement('div');
      modalRoot.id = 'ui-v4-modal-root';
      document.body.appendChild(modalRoot);
    }
  }, []);

  // Toasty
  const [toastManagerPosition, setToastManagerPosition] =
    useState<ToastManagerPosition | null>(null);
  const [toasts, setToasts] = useState<ToastInfo[]>([]);
  const toastManagerClassName = classNames(Classes.TOAST_MANAGER, {
    [`${Classes.TOAST_MANAGER}--${toastManagerPosition}`]:
      toastManagerPosition !== null,
    [`${Classes.TOAST_MANAGER}--no-position`]: toastManagerPosition === null,
  });

  const getTopModal = useCallback(() => {
    let modalInfos = Object.values(modals.current);
    return modalInfos.length > 0
      ? modalInfos.reduce((prev, curr) => {
          return prev.depth > curr.depth ? prev : curr;
        })
      : undefined;
  }, []);

  const modalHide = useCallback((key?: string | any) => {
    const topModal =
      key && typeof key === 'string' ? modals.current[key] : getTopModal();

    if (topModal) {
      delete modals.current[topModal.key];
    }
    setModalsState(Object.values(modals.current));
  }, []);

  const modalShow = useCallback((event: IShow) => {
    const { component, componentProps } = event;
    const key = 'modal-' + keyref.current++;

    const topModal = getTopModal();
    const depth = topModal ? topModal.depth + 1 : 0;
    let modalInfo = {
      key,
      depth,
      component,
      componentProps,
    };
    modals.current[key] = modalInfo;

    setModalsState(Object.values(modals.current));
  }, []);

  const handleClose = (key: string) => (e?: React.MouseEvent<HTMLElement>) => {
    const modal = modals.current[key];
    if (!modal) {
      return;
    }

    // Custom on close
    let onClose;
    if (modal.componentProps && modal.componentProps.onClose) {
      onClose = modal.componentProps.onClose;
    } else {
      onClose = undefined;
    }

    let defaultPrevented = false;
    onClose &&
      onClose({
        ...(e || ({} as React.MouseEvent<HTMLElement>)),
        preventDefault: () => {
          defaultPrevented = true;
          e && e.preventDefault();
        },
        stopPropagation: () => {
          defaultPrevented = true;
          e && e.stopPropagation();
        },
      } as React.MouseEvent<HTMLElement>);
    if (!defaultPrevented) {
      modalHide(key);
    }
  };

  const getSortedModals = useCallback(() => {
    return Object.values(modals.current).sort((a, b) => {
      return a.depth - b.depth;
    });
  }, []);

  const getToasts = () => toasts;

  useEffect(() => {
    initializeToastManager({
      getFn: getToasts,
      setFn: setToasts,
      setPositionFn: setToastManagerPosition,
    });

    eventEmitter.addListener(MODAL_SHOW, modalShow);
    eventEmitter.addListener(MODAL_HIDE, modalHide);

    return () => {
      eventEmitter.removeAllListeners(MODAL_SHOW);
      eventEmitter.removeAllListeners(MODAL_HIDE);
    };
  }, []);

  const dialogs = getSortedModals().map((modal) => {
    let element = React.createElement(modal.component, {
      ...modal.componentProps,
      key: 'modal-' + modal.key,
      onClose: handleClose(modal.key),
    });
    return element;
  });

  const onCloseToast = useCallback((key: string) => {
    setToasts((prev) => prev.filter((toast) => toast.key !== key));
  }, []);

  return (
    <>
      <div className={toastManagerClassName}>
        {toasts.map((toast) => (
          <Toast
            key={toast.key}
            toast={toast}
            onClose={() => onCloseToast(toast.key)}
          />
        ))}
      </div>
      {dialogs}
    </>
  );
};
